import {
  CommunityStatus,
  CommunityJoinMode,
  CommunityListingVisibility,
  CommunityMembershipStatus,
  CommunityAccessType,
  PermissionKey,
  UserRole,
} from '../enums';
import { DomainError } from '../errors/domain-error';
import { hasPermission } from '../rbac';
import { isReservedUsername } from '../identity/username';
import { grantsCommunityMembershipEntitlement } from '../commerce/community-membership-billing';
import { COMMUNITY_SLUG_MAX_CHARS } from '../entities/community';
import { validateCommunityMemberFieldAnswersTyped } from './community-member-field.policy';

export const COMMUNITY_SLUG_RESERVED_MESSAGE = 'That community URL is reserved';

export function authorizeCommunityManagement(role: UserRole): void {
  if (!hasPermission(role, PermissionKey.CAN_MANAGE_COMMUNITIES)) {
    throw new DomainError('Cannot manage communities');
  }
}

export type CommunityPublishValidation =
  | { ok: true }
  | { ok: false; message: string };

export function validateCommunityPublishRequirements(
  title: string,
  about: string | null | undefined,
): CommunityPublishValidation {
  if (!title.trim()) {
    return { ok: false, message: 'Add a community title before publishing' };
  }
  if (!about?.trim()) {
    return { ok: false, message: 'Add an about section before publishing' };
  }
  return { ok: true };
}

export function slugifyCommunityTitle(title: string): string {
  const base = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, COMMUNITY_SLUG_MAX_CHARS);
  return base || 'community';
}

export function normalizeCommunitySlugInput(input: string): string {
  return slugifyCommunityTitle(input);
}

export function buildCommunitySlugCandidate(base: string, index: number): string {
  const normalized = base.slice(0, COMMUNITY_SLUG_MAX_CHARS);
  return index === 0 ? normalized : `${normalized}-${index}`;
}

export function deconflictReservedCommunitySlugBase(base: string): string {
  const trimmed = base.replace(/-circle$/, '');
  return `${trimmed}-circle`.slice(0, COMMUNITY_SLUG_MAX_CHARS);
}

/** Resolve a publishable slug from user input, deconflicting reserved platform words. */
export function resolveCommunitySlugCandidate(input: string): {
  slug: string;
  adjustedFrom: string | null;
  validation: CommunityPublishValidation;
} {
  let candidate = normalizeCommunitySlugInput(input);
  let adjustedFrom: string | null = null;

  if (!candidate) {
    return {
      slug: '',
      adjustedFrom: null,
      validation: { ok: false, message: 'Community URL slug is required' },
    };
  }

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const validation = validateCommunitySlug(candidate);
    if (validation.ok) {
      return { slug: candidate, adjustedFrom, validation: { ok: true } };
    }
    if (validation.message === COMMUNITY_SLUG_RESERVED_MESSAGE) {
      if (!adjustedFrom) adjustedFrom = candidate;
      candidate = deconflictReservedCommunitySlugBase(candidate);
      continue;
    }
    return { slug: candidate, adjustedFrom, validation };
  }

  return {
    slug: candidate,
    adjustedFrom,
    validation: { ok: false, message: COMMUNITY_SLUG_RESERVED_MESSAGE },
  };
}

export function validateCommunitySlug(slug: string): CommunityPublishValidation {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) {
    return { ok: false, message: 'Community URL slug is required' };
  }
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
    return { ok: false, message: 'Community URL slug must use lowercase letters, numbers, and hyphens' };
  }
  if (isReservedUsername(normalized)) {
    return { ok: false, message: COMMUNITY_SLUG_RESERVED_MESSAGE };
  }
  return { ok: true };
}

export function defaultCommunityStatus(requested?: CommunityStatus): CommunityStatus {
  return requested ?? CommunityStatus.DRAFT;
}

export function isCommunityPubliclyVisible(status: CommunityStatus | string): boolean {
  return status === CommunityStatus.PUBLISHED;
}

export function isCommunitySecretListing(
  listingVisibility: CommunityListingVisibility | string | null | undefined,
): boolean {
  return listingVisibility === CommunityListingVisibility.SECRET;
}

export function isCommunityListedInPublicDirectory(
  listingVisibility: CommunityListingVisibility | string | null | undefined,
): boolean {
  return (listingVisibility ?? CommunityListingVisibility.LISTED) === CommunityListingVisibility.LISTED;
}

export function isActiveCommunityMembership(
  status: CommunityMembershipStatus | string | null | undefined,
): boolean {
  return status === CommunityMembershipStatus.ACTIVE;
}

export function assertCommunityMembershipEntitlement(
  membership:
    | {
        status: CommunityMembershipStatus | string;
        stripeBillingStatus?: string | null;
        graceEndsAt?: Date | string | null;
        cancelAtPeriodEnd?: boolean | null;
        currentPeriodEnd?: Date | string | null;
      }
    | null
    | undefined,
  accessType: CommunityAccessType | string = CommunityAccessType.FREE,
): asserts membership is NonNullable<typeof membership> {
  if (!grantsCommunityMembershipEntitlement(membership, accessType)) {
    throw new DomainError('Community membership required');
  }
}

export function validateCommunityJoinInvite(params: {
  joinMode: CommunityJoinMode | string;
  inviteToken?: string | null;
  communityInviteToken?: string | null;
}): CommunityPublishValidation {
  if (params.joinMode !== CommunityJoinMode.INVITE_ONLY) {
    return { ok: true };
  }
  const provided = params.inviteToken?.trim();
  const expected = params.communityInviteToken?.trim();
  if (!provided || !expected || provided !== expected) {
    return { ok: false, message: 'A valid invite link is required to join this community' };
  }
  return { ok: true };
}

export function validateCommunityJoinGuidelines(
  accepted: boolean,
  hasRules: boolean,
): CommunityPublishValidation {
  if (hasRules && !accepted) {
    return { ok: false, message: 'Accept the community guidelines before joining' };
  }
  return { ok: true };
}

export function validateCommunityJoinIntakeAnswers(
  questions: Array<{ id: string; prompt: string; required: boolean; enabled: boolean }>,
  answers: Record<string, string> | undefined,
  maxAnswerChars = 1000,
): CommunityPublishValidation {
  const enabled = questions.filter((question) => question.enabled);
  if (enabled.length === 0) return { ok: true };

  const provided = answers ?? {};
  for (const question of enabled) {
    const value = provided[question.id]?.trim();
    if (question.required && !value) {
      return { ok: false, message: `Answer required: ${question.prompt}` };
    }
    if (value && value.length > maxAnswerChars) {
      return {
        ok: false,
        message: `Answer for "${question.prompt}" must be ${maxAnswerChars} characters or fewer`,
      };
    }
  }
  return { ok: true };
}

export function validateCommunityMemberFieldAnswers(
  fields: Array<{
    id: string;
    prompt: string;
    required: boolean;
    enabled: boolean;
    fieldType?: string | null;
    options?: string[] | null;
    conditionalRules?: { showWhenFieldId?: string | null; showWhenEquals?: string | null } | null;
  }>,
  answers: Record<string, string> | undefined,
  maxAnswerChars = 1000,
): CommunityPublishValidation {
  const typedFields = fields.map((field) => ({
    id: field.id,
    prompt: field.prompt,
    required: field.required,
    enabled: field.enabled,
    fieldType: field.fieldType,
    options: field.options,
    conditionalRules: field.conditionalRules,
  }));
  return validateCommunityMemberFieldAnswersTyped(typedFields, answers, maxAnswerChars);
}

export function validateCommunityAdultContentConfirmation(
  adultContentEnabled: boolean,
  minimumAgeYears: number | null | undefined,
  confirmed: boolean | undefined,
): CommunityPublishValidation {
  if (!adultContentEnabled) return { ok: true };
  if (!confirmed) {
    const age = minimumAgeYears ?? 18;
    return {
      ok: false,
      message: `You must confirm you are at least ${age} years old to join this community`,
    };
  }
  return { ok: true };
}

export function normalizeCommunityMinimumAgeYears(value?: number | null): number {
  const age = value ?? 18;
  return Math.min(Math.max(age, 18), 21);
}

const ALLOWED_STATUS_TRANSITIONS: Record<CommunityStatus, CommunityStatus[]> = {
  [CommunityStatus.DRAFT]: [CommunityStatus.PUBLISHED, CommunityStatus.ARCHIVED],
  [CommunityStatus.PUBLISHED]: [CommunityStatus.DRAFT, CommunityStatus.ARCHIVED],
  [CommunityStatus.ARCHIVED]: [CommunityStatus.DRAFT],
};

export function assertCommunityStatusTransition(
  current: CommunityStatus,
  next: CommunityStatus,
): void {
  if (current === next) return;
  const allowed = ALLOWED_STATUS_TRANSITIONS[current] ?? [];
  if (!allowed.includes(next)) {
    throw new DomainError(`Cannot change community status from ${current} to ${next}`);
  }
}

/** Platform admin may force-archive any non-archived Circle. */
export function canAdminTakedownCommunity(status: CommunityStatus | string): boolean {
  return status !== CommunityStatus.ARCHIVED;
}

export function assertAdminCommunityTakedown(status: CommunityStatus | string): void {
  if (!canAdminTakedownCommunity(status)) {
    throw new DomainError('Community is already taken down');
  }
}
